/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';
import {ModelResponse, ResponseData} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {getConnections, testConnection} from "../util/requestSender";
import {ConnectionStatus} from "../util/interfaces";

interface Props extends BaseProps {
    name?: string;
    connected?: boolean;
    status_changed_at?: string;
    last_checked?: string;
    status?: ConnectionStatus;
}

interface Fields extends BaseFields {
    name: string;
    connected: boolean;
    status_changed_at: string;
    last_checked: string;
    status: ConnectionStatus;
}

const fields: Fields = {
    id: '',
    created_at: '',
    updated_at: '',
    name: '',
    connected: true,
    status_changed_at: '',
    last_checked: '',
    status: ConnectionStatus.UP,
};

const fieldsDefinition: { [key in keyof Fields]: FieldDefinition } = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    name: createFieldDef(ModelFieldType.STRING),
    connected: createFieldDef(ModelFieldType.BOOLEAN),
    status_changed_at: createFieldDef(ModelFieldType.STRING),
    last_checked: createFieldDef(ModelFieldType.STRING),
    status: createFieldDef(ModelFieldType.STRING, ConnectionStatus.UP),
};

const ignoreProps: Array<keyof Props> = [];

export default class Connection extends BaseModel<Props, Fields> {
    static test = (name: string, callback: ModelResponse<Connection | null>) => {
        testConnection({
            name, callback: (data: ResponseData) => {
                const connection: Connection | null = data.success ? new Connection(data.connection) : null;
                callback(connection, data.success, data);
            }
        });
    };

    static getAll = (callback: ModelResponse<Connection[]>) => {
        getConnections({
            callback: (data: ResponseData) => {
                const connections: Connection[] = data.success ?
                    data.connections.map((conn: any) => new Connection(conn)) : [];
                callback(connections, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get name(): string {
        return this.fields.name;
    }

    set name(name: string) {
        this.fields.name = name;
    }

    get translatedName(): string {
        if (this.name === 'bol') return 'Bol';
        else if (this.name === 'mailerlite') return 'MailerLite';
        return '';
    }

    get connected(): boolean {
        return this.fields.connected;
    }

    set connected(connected: boolean) {
        this.fields.connected = connected;
    }

    get lastChecked(): string {
        return this.fields.last_checked;
    }

    set lastChecked(last_checked: string) {
        this.fields.last_checked = last_checked;
    }

    get lastCheckedMoment(): IDate {
        return new IDate(this.lastChecked);
    }

    get statusChangedAtDate(): IDate | null {
        if (!this.fields.status_changed_at) return null;
        return new IDate(this.fields.status_changed_at);
    }

    get status(): ConnectionStatus {
        return this.fields.status;
    }

    protected build = (props?: Props) => {
        this.constructFields(props);
    };
}